import React, { useState } from "react";
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import dayjs from "dayjs";
import { useAnalystEstimates } from "../Dashboard/Charts/hooks/useAnalystEstimates";
import { useStockCurrency } from "../../contexts/StockCurrencyContext";
import { formatNumber } from "../Dashboard/Charts/utils/formatNumber";
import {
  formatStockPrice,
  getDisplayCurrencySymbol,
} from "../../utils/stockPriceFormatter";

interface AnalystEstimatesTableProps {
  ticker: string;
}

type PeriodType = "annual" | "quarter";

export const AnalystEstimatesTable: React.FC<AnalystEstimatesTableProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { currency } = useStockCurrency();
  const [period, setPeriod] = useState<PeriodType>("annual");

  // Get display currency symbol (handles GBX → £)
  const displaySymbol = getDisplayCurrencySymbol(currency);

  // Format EPS values - these are per-share prices that need pence conversion
  const formatEps = (value: number | null | undefined) => {
    if (value == null) return "—";
    return formatStockPrice({ price: value, currency });
  };

  // Format large revenue values - these are already in base currency (GBP not GBX)
  // so we just need the display symbol
  const formatRevenue = (value: number | null | undefined) => {
    if (value == null) return "—";
    return `${displaySymbol}${formatNumber(value)}`;
  };

  const { data, isLoading, error } = useAnalystEstimates(ticker, {
    period,
    limit: period === "annual" ? 5 : 8,
  });

  const flatColors = theme.customColors?.flat?.[isDark ? "dark" : "light"] ?? {
    borderSecondary: isDark
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)",
  };

  const handlePeriodChange = (
    _: React.MouseEvent<HTMLElement>,
    newPeriod: PeriodType | null
  ) => {
    if (newPeriod) {
      setPeriod(newPeriod);
    }
  };

  // Common header cell style
  const headerCellSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    textAlign: "center" as const,
  };

  // Common data cell style
  const dataCellSx = {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    textAlign: "center" as const,
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.surface,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Skeleton variant="text" width={140} height={20} />
          <Skeleton variant="rounded" width={140} height={32} />
        </Box>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={40}
            sx={{ mb: 1, borderRadius: 0.5 }}
          />
        ))}
      </Box>
    );
  }

  // Error or no data state
  if (error || !data || data.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.surface,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          p: 3,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.primary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.875rem",
            fontWeight: 600,
            mb: 2,
          }}
        >
          Analyst Estimates
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Analyst estimates data not available for this stock
        </Typography>
      </Box>
    );
  }

  // Sort data by date (nearest first)
  const sortedData = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.surface,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        p: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.primary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          Analyst Estimates
        </Typography>

        {/* Period Toggle */}
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
              },
            },
          }}
        >
          <ToggleButton value="annual">Annual</ToggleButton>
          <ToggleButton value="quarter">Quarterly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: 600,
            tableLayout: "fixed",
            borderCollapse: "collapse",
          }}
        >
          {/* Header Row */}
          <Box component="thead">
            <Box
              component="tr"
              sx={{
                borderBottom: `1px solid ${flatColors.borderSecondary}`,
              }}
            >
              <Box
                component="th"
                sx={{ ...headerCellSx, textAlign: "left", pb: 1, width: "12%" }}
              >
                {period === "annual" ? "Year" : "Quarter"}
              </Box>
              <Box component="th" sx={{ ...headerCellSx, pb: 1, width: "13%" }}>
                Revenue<br />Low
              </Box>
              <Box component="th" sx={{ ...headerCellSx, pb: 1, width: "13%" }}>
                Revenue<br />Avg
              </Box>
              <Box component="th" sx={{ ...headerCellSx, pb: 1, width: "13%" }}>
                Revenue<br />High
              </Box>
              <Box component="th" sx={{ ...headerCellSx, pb: 1, width: "12%" }}>
                EPS<br />Low
              </Box>
              <Box component="th" sx={{ ...headerCellSx, pb: 1, width: "12%" }}>
                EPS<br />Avg
              </Box>
              <Box component="th" sx={{ ...headerCellSx, pb: 1, width: "12%" }}>
                EPS<br />High
              </Box>
              <Box
                component="th"
                sx={{ ...headerCellSx, textAlign: "right", pb: 1, width: "8%" }}
              >
                #
              </Box>
            </Box>
          </Box>

          {/* Data Rows */}
          <Box component="tbody">
            {sortedData.map((estimate, index) => {
              const fiscalYear =
                period === "annual"
                  ? `FY ${dayjs(estimate.date).format("YYYY")}`
                  : dayjs(estimate.date).format("Q[Q] 'YY");

              return (
                <Box
                  component="tr"
                  key={estimate.date}
                  sx={{
                    borderBottom:
                      index < sortedData.length - 1
                        ? `1px solid ${flatColors.borderSecondary}`
                        : "none",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(0,0,0,0.02)",
                    },
                  }}
                >
                  {/* Fiscal Year */}
                  <Box
                    component="td"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      py: 1.25,
                    }}
                  >
                    {fiscalYear}
                  </Box>

                  {/* Revenue Low */}
                  <Box component="td" sx={{ ...dataCellSx, py: 1.25 }}>
                    {formatRevenue(estimate.revenueLow)}
                  </Box>

                  {/* Revenue Avg */}
                  <Box component="td" sx={{ ...dataCellSx, py: 1.25 }}>
                    {formatRevenue(estimate.revenueAvg)}
                  </Box>

                  {/* Revenue High */}
                  <Box component="td" sx={{ ...dataCellSx, py: 1.25 }}>
                    {formatRevenue(estimate.revenueHigh)}
                  </Box>

                  {/* EPS Low */}
                  <Box component="td" sx={{ ...dataCellSx, py: 1.25 }}>
                    {formatEps(estimate.epsLow)}
                  </Box>

                  {/* EPS Avg */}
                  <Box component="td" sx={{ ...dataCellSx, py: 1.25 }}>
                    {formatEps(estimate.epsAvg)}
                  </Box>

                  {/* EPS High */}
                  <Box component="td" sx={{ ...dataCellSx, py: 1.25 }}>
                    {formatEps(estimate.epsHigh)}
                  </Box>

                  {/* Number of Analysts */}
                  <Box
                    component="td"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: theme.palette.text.secondary,
                      textAlign: "right",
                      py: 1.25,
                    }}
                  >
                    {estimate.numAnalystsRevenue ?? estimate.numAnalystsEps ?? "—"}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AnalystEstimatesTable;
